import { Ref, ref } from 'vue';
import { useState } from '@/composables/common/useState';
import { AxiosError } from 'axios';
import { useToggle } from '@/composables/useToggle';

interface FetchReturnType<T, F extends (...args: any[]) => void> {
  query: Ref<string>;
  data: Ref<T>;
  loading: Ref<boolean>;
  error: Ref<AxiosError | null>;
  callFetch: (...args: Parameters<F>) => Promise<void>;
}

type DefaultFunction = (...args: any[]) => any;

export function useFetch<T, F extends DefaultFunction>(
  fetchCallback: F
): FetchReturnType<T | undefined, F>;
export function useFetch<T, F extends DefaultFunction>(
  fetchCallback: F,
  initialValue: T
): FetchReturnType<T, F>;
export function useFetch<T, F extends DefaultFunction>(
  fetchCallback: F,
  initialValue?: T
): FetchReturnType<T | undefined, F> {
  const query = ref('');

  const [data, setData] = useState<T | undefined>(initialValue);
  const [error, setError] = useState<AxiosError | null>(null);
  const [loading, toggleLoading] = useToggle();

  const callFetch = async (...args: Parameters<F>) => {
    toggleLoading(true);

    try {
      setData(await fetchCallback(...args));
    } catch (e: unknown) {
      setError(e as AxiosError);
      throw e;
    } finally {
      toggleLoading(false);
    }
  };

  return { callFetch, query, loading, data, error };
}
